import { Dispatch, useContext, useEffect, useState } from 'react';
import {
  ProductListingContext,
  linkCombinationsToTmpAttributeKey,
} from '../functions/data_formatting_functions';
import {
  actionSetAllAttributes,
  actionSetListingData,
  actionSetProductType,
  actionSetUserType,
} from '../store/create_listing_actions';
import Information from '../screens/item_information/item_information_screen';
import Media from '../screens/media/media_screen';
import Preview from '../screens/preview/preview_screen';
import { STEPS, FLOW_TYPE, FLOW_TYPE_MAPPING } from '../constants';
import { useDispatch } from 'react-redux';
import Shipping from '../screens/shipping/shipping_screen';
import Specifications from '../screens/specifications/specifications_screen';
import CombinationsTable from '../screens/combinations/combinations_screen';
import { Wrapper, ListingStyledWrapper } from './create_item_flow.styled';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  convertDataFormat,
  get_all_attributes,
} from '../functions/data_formatting_functions';
import {
  CardStyled,
  FlexContainer,
  HeadingStyled,
} from '../../../styles/style';
import { S3ApiRepository } from '../../../data/repositories/s3_repository';
import { ApiRepository } from '../../../data/repositories/base_repository';
import { StoreDetails } from '../../../data/types/stores';
import { LISTING_TYPES, LISTING_TYPES_NAME } from '../../../config/constants';
import { toast } from 'react-toastify';
import {
  GetAppropriateFlowButtons,
  // getAppropriateSuccessMessage, //TODO remove dis shit
  getAppropriateTitle,
  getFlowType,
} from '../functions/create_item_functions';
import SharedRepository from '../../../data/repositories/shared_repository';
import {
  Listing,
  ListingItem,
  ListingRequestStatus,
  ListingState,
  TableCombination,
} from '../../../data/types/listings';
import { sanitizeHTML } from '../../../utils/functions/formatting_functions';
import { Button, Tooltip } from '@nextui-org/react';
import { FaRegMessage } from 'react-icons/fa6';

//TODO add verification before changing to the next step! (When clicking on the numbers and not the next Step button)
//TODO fix numeric attributes overflowing
//TODO boolean attributes are bugged in the payload?
type CreateItemProps = {
  isManagement: boolean;
  currentStore?: StoreDetails;
  actionCreateListing: (payload: any) => {
    type: string;
    payload: any;
  };
  actionGetStoreImages?: (payload: any) => {
    type: string;
    payload: any;
  };
  s3Repository: S3ApiRepository;
  filesRepository: ApiRepository;
  setPromotionVisibilityAndItems?: (items: ListingItem[]) => void;
};

const CreateItem = ({
  isManagement,
  currentStore,
  actionCreateListing,
  actionGetStoreImages,
  s3Repository,
  filesRepository,
  setPromotionVisibilityAndItems,
}: CreateItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    state,
    dispatch,
  }: {
    state: ListingState;
    dispatch: Dispatch<any>;
  } = useContext(ProductListingContext);

  const use_dispatch = useDispatch();
  const [step, setStep] = useState(STEPS.SET_UP);
  const [prevScreen, setPrevScreen] = useState(null);
  const [flow] = useState(getFlowType(isManagement, location));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    toast.info(FLOW_TYPE_MAPPING[flow]);
  }, [location]);

  const addStep = (toAdd = 1) => {
    setStep(step + toAdd);
  };

  const removeStep = () => {
    setStep(step - 1);
  };

  const canChangeStep = (changeStep: number) => {
    if (step === STEPS.SET_UP) {
      toast.warning('Please wait for the data to load before proceeding.');
      return;
    }
    if (
      step === STEPS.SPECIFICATIONS &&
      changeStep !== STEPS.COMBINATIONS_TABLE
    ) {
      toast.warning(
        'To update selected specifications, please click "Next Step" in order to link selected attributes to the combinations table.',
      );
      return;
    }
    if (
      changeStep === STEPS.COMBINATIONS_TABLE &&
      step === STEPS.SPECIFICATIONS
    ) {
      toast.warning(
        'You must link the selected attributes to the combinations table before proceeding to the next step. Please click "Next Step" to link the selected attributes to the combinations table.',
      );
      return;
    }
    if (!isManagement && changeStep > step) {
      toast.warning(
        'Please complete the previous step first, then click Next Step',
      );
      return;
    }
    setStep(changeStep);
  };

  function matchAndFillValuesImages(obj: any) {
    const { select_attrs, selectedImages } = obj;

    select_attrs.forEach((selectAttr: any) => {
      const { name_en, values_en } = selectAttr;
      const selectedValues = selectedImages[name_en];

      const valuesImages: number[] = values_en
        ?.filter(
          (value: any) => selectedValues && selectedValues[value] !== undefined,
        )
        ?.map((value: any) => selectedValues[value] as number);

      // Add valuesImages to selectAttr
      selectAttr.values_images = valuesImages;
    });

    return obj;
  }

  async function fetchData() {
    var locationState = isManagement ? location?.state?.row : location?.state;
    if (!locationState) {
      toast.info('Parsing data from the local storage, please wait...');
      const savedState = localStorage.getItem('templateState');
      if (savedState) {
        locationState = JSON.parse(savedState);
        // Use the state as needed
        // Optionally remove the state from localStorage after use
        localStorage.removeItem('templateState');
      }
    }
    dispatch(actionSetUserType(isManagement ? 1 : 0));
    dispatch(actionSetProductType(locationState?.items?.length > 1 ? 1 : 0));
    if (locationState) {
      const prevScreen = location?.state?.prevScreen;
      setPrevScreen(prevScreen);
      let data = await convertDataFormat(locationState);
      const category = await SharedRepository.getCategoryDetails(
        locationState.category_id,
      );
      if (!category) {
        return;
      }
      const {
        all_attributes,
        select_attrs,
        boolean_attrs,
        numeric_attrs,
        combinations,
      } = await get_all_attributes(locationState, category?.attributes);
      const selected_Images: {
        [attrName: string]: { [value: string]: number };
      } = {};
      all_attributes.map((attr: any) => {
        // add attribute name as well
        selected_Images[attr.name_en] = {};
        attr.values_images?.map((imageId: number, index: number) => {
          if (imageId) {
            selected_Images[attr.name_en][attr.values_en[index]] = imageId;
          }
        });
      });
      if (combinations.length === 1) {
        data = {
          ...data,
          price: combinations[0].price,
          in_stock: combinations[0].in_stock,
          barcode: combinations[0].barcode,
          applied_promotion: combinations[0].applied_promotion,
          original_price: combinations[0].original_price,
        };
      }
      await linkCombinationsToTmpAttributeKey(state, dispatch, combinations);
      console.log('the draft/template/old-version state is:', locationState);
      console.log('data after formatting is', data);
      console.log('from now, attributes are:', all_attributes);
      console.log('state', state);
      dispatch(
        actionSetListingData({
          ...data,
          category: category,
          resubmission: false, //TODO check why it's marked as true?
          select_attrs: select_attrs,
          boolean_attrs: boolean_attrs,
          numeric_attrs: numeric_attrs,
          selectedImages: selected_Images,
          combinations_table: combinations,
          prev_combinations_table: combinations,
        }),
      );
      dispatch(actionSetAllAttributes([...all_attributes]));
    }
    setStep(STEPS.ITEM_INFORMATION);
  }

  const requestPublish = async (type: number) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    const obj = JSON.parse(JSON.stringify(state.data));

    obj.listing_type =
      LISTING_TYPES_NAME[type as keyof typeof LISTING_TYPES_NAME];

    if (type === LISTING_TYPES.template) {
      // Relevant to Management & Store
      if (
        flow !== FLOW_TYPE.StoreUpdateListing &&
        flow !== FLOW_TYPE.StoreNewListingUsingDraft &&
        flow !== FLOW_TYPE.StoreNewListingUsingTemplate &&
        obj.id
      ) {
        obj.id_of_draft_to_delete = obj.id;
        obj.edit = true;
      } else {
        delete obj.parent_listing_id;
        delete obj.id_of_draft_to_delete;
      }
    }
    if (!isManagement) {
      if (type === LISTING_TYPES.draft) {
        // Relevant to Store Only
        if (flow !== FLOW_TYPE.StoreNewListingUsingDraft) {
          delete obj.parent_listing_id;
          delete obj.id_of_draft_to_delete;
        }
      }

      if (type === LISTING_TYPES.public_listing) {
        // Relevant to Store Only
        if (prevScreen == 'publish') {
          obj.parent_listing_id = obj.id;
        } else if (prevScreen == 'draft') {
          obj.id_of_draft_to_delete = obj.id;
        } else {
          delete obj.parent_listing_id;
          delete obj.id_of_draft_to_delete;
        }
        if (obj.edit) {
          obj.parent_listing_id = obj.id;
        }
      }
    }

    if (!obj.edit || location?.state?.duplicate === true) {
      delete obj.parent_listing_id;
      delete obj.id_of_draft_to_delete;
    }

    // Filter notToUploadImages
    if (obj.notToUploadImages?.length > 0) {
      obj.images = obj.images.filter(
        (id: any) => !obj.notToUploadImages.includes(id),
      );
    }

    // Set listing image
    if (
      (obj.cover_image_id === undefined && obj?.images?.length > 0) ||
      (obj.images?.length > 0 &&
        obj.images.includes(obj.cover_image_id) === false)
    )
      obj.cover_image_id = obj?.images[0];

    // Combinations table processing
    obj.combinations_table =
      state.data.combinations_table?.filter(
        (comb: TableCombination) => comb.valid,
      ) || [];
    obj.combinations_table?.map((comb: any) => {
      Object.keys(comb).map((item) => {
        if (comb[item] === 'true') {
          comb[item] = true;
        } else if (comb[item] === 'false') {
          comb[item] = false;
        }
      });
      Object.keys(comb?.attrs || {}).map((key) => {
        if (comb.attrs[key] === 'true') {
          comb.attrs[key] = true;
        } else if (comb.attrs[key] === 'false') {
          comb.attrs[key] = false;
        }
      });
    });

    if (obj?.combinations_table?.length <= 1) {
      obj.combinations_table = [
        {
          price: obj?.price,
          in_stock: obj?.in_stock,
          attrs: {},
          valid: true,
          barcode: obj?.barcode,
          image_id: obj?.cover_image_id,
          attribute_temp_id__attribute_value_index: {},
        },
      ];
    } else {
      const linked = await linkCombinationsToTmpAttributeKey(
        state,
        dispatch,
        obj.combinations_table,
      );
      obj.combinations_table = linked.combinations;
      obj.attributes = linked.attributesWithTempId;
      obj.select_attrs = linked.selectAttrsWithTempId;
      obj.boolean_attrs = linked.booleanAttrsWithTempId;
      obj.numeric_attrs = linked.numericAttrsWithTempId;
    }

    // Sanitize HTML
    obj.description_en.replace('text-align:left;direction:ltr', '');
    obj.description_en = obj.description_en.replace(
      /style\s*=\s*"[^"]*text-align:left;direction:ltr[^"]*"/gi,
      '',
    );
    obj.description_en.replace('text-align:right;direction:rtl', '');
    obj.description_en = obj.description_en.replace(
      /style\s*=\s*"[^"]*text-align:right;direction:rtl[^"]*"/gi,
      '',
    );
    obj.description_en.replace('text-align:right;direction:ltr', '');
    obj.description_en = obj.description_en.replace(
      /style\s*=\s*"[^"]*text-align:right;direction:ltr[^"]*"/gi,
      '',
    );
    obj.description_en.replace('text-align:left;direction:rtl', '');
    obj.description_en = obj.description_en.replace(
      /style\s*=\s*"[^"]*text-align:left;direction:rtl[^"]*"/gi,
      '',
    );

    // X
    obj.description_ar.replace('text-align:left;direction:ltr', '');
    obj.description_ar = obj.description_ar.replace(
      /style\s*=\s*"[^"]*text-align:left;direction:ltr[^"]*"/gi,
      '',
    );
    obj.description_ar.replace('text-align:right;direction:rtl', '');
    obj.description_ar = obj.description_ar.replace(
      /style\s*=\s*"[^"]*text-align:right;direction:rtl[^"]*"/gi,
      '',
    );
    obj.description_ar.replace('text-align:right;direction:ltr', '');
    obj.description_ar = obj.description_ar.replace(
      /style\s*=\s*"[^"]*text-align:right;direction:ltr[^"]*"/gi,
      '',
    );
    obj.description_ar.replace('text-align:left;direction:rtl', '');
    obj.description_ar = obj.description_ar.replace(
      /style\s*=\s*"[^"]*text-align:left;direction:rtl[^"]*"/gi,
      '',
    );

    obj.description_en = sanitizeHTML(obj.description_en).__html;
    obj.description_ar = sanitizeHTML(obj.description_ar).__html;

    // Remove unnecessary fields
    delete obj.official_attributes;
    delete obj.custom_attributes;
    delete obj.attributes;
    delete obj.items;
    delete obj.imagesList;
    delete obj.notToUploadImages;

    // Set payload
    const payload = {
      data: matchAndFillValuesImages(obj),
      success: (res: Listing) => {
        if (setPromotionVisibilityAndItems !== undefined) {
          setPromotionVisibilityAndItems(res.items);
        } else {
          setTimeout(() => {
            navigate(isManagement ? '/templates' : '/products');
          }, 200);
        }
      },
      error: () => {
        setSubmitting(false);
      },
    };
    await use_dispatch(actionCreateListing(payload));
  };

  const NeedHelp = (helpStep: number) => {
    const urlMap = {
      [STEPS.ITEM_INFORMATION]:
        'https://www.loom.com/share/f4055bcaaae34f53ba2c92def1c7fdab?sid=668a44dc-fd7d-4bb5-80e7-4a96def937f9',
      [STEPS.MEDIA]:
        'https://www.loom.com/share/ed9da7be0a5c40cc92106452b47f522e?sid=828cf25b-2518-46b9-b3c9-3d470426ce14',
      [STEPS.SPECIFICATIONS]: [
        'https://www.loom.com/share/9f0aae1e64f147909d56c951d0d2e15a?sid=d5ff3437-662e-486f-b0c1-eb5ed1d65da1',
        'https://www.loom.com/share/6c6aff51e7524ee4b8423a7d361ed294?sid=c9054d12-e2a0-4c1c-ae89-c871686d2b27',
        'https://www.loom.com/share/11734b1f47bb4d4f80740482d1f886bb?sid=33ff94a8-99cc-4270-b51c-3a5983ba511c',
      ],
      [STEPS.COMBINATIONS_TABLE]:
        'https://www.loom.com/share/5bb83395cc434f06858d7ea385f0e587?sid=2bc0c280-0772-417b-bf25-5bb6f95f42d8',
      [STEPS.SHIPPING]:
        'https://www.loom.com/share/0f260d6e0260421187e58130e5064985?sid=f6ecf9ab-4d92-4bd7-9639-6d53a4593dc4',
      // [STEPS.PREVIEW]: '/help#preview',
    };

    return step === STEPS.PREVIEW || step !== helpStep ? (
      <> </>
    ) : (
      <>
        <Link
          to={
            Array.isArray(urlMap[helpStep])
              ? urlMap[helpStep][0]
              : urlMap[helpStep]
          }
          target="_blank"
          className="help-text"
        >
          need help?
        </Link>
        {Array.isArray(urlMap[helpStep]) && (
          <div className="help-numbers">
            {urlMap[helpStep].map((url, index) => (
              <Link
                key={index}
                to={url}
                target="_blank"
                className="help-number"
              >
                {index + 1}
              </Link>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <Wrapper>
      <ListingStyledWrapper />
      <div className="w-100 px-3">
        <FlexContainer className="justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <HeadingStyled
              style={{
                fontSize: '25px',
                fontWeight: '700',
              }}
            >
              {getAppropriateTitle(flow)}
              {state.data.request?.status === ListingRequestStatus.rejected &&
                state.data.request?.response?.msg && (
                  <Tooltip
                    closeDelay={1000}
                    content={
                      <div className="px-1 py-1">
                        <div className="text-small font-bold">
                          Silal Quality Team:
                        </div>
                        <div className="text-tiny">
                          {state.data.request?.response?.msg}
                        </div>
                      </div>
                    }
                  >
                    <Button
                      isIconOnly
                      color="warning"
                      variant="light"
                      className="mt-3"
                    >
                      <FaRegMessage size={20} />
                    </Button>
                  </Tooltip>
                )}
            </HeadingStyled>
          </div>
          {GetAppropriateFlowButtons(
            requestPublish,
            flow,
            isManagement,
            submitting,
          )}
        </FlexContainer>
        <CardStyled>
          <div className="stepper-wrapper">
            <div
              className={`stepper-item ${
                step >= STEPS.ITEM_INFORMATION ? 'active' : ''
              }`}
              onClick={() => canChangeStep(STEPS.ITEM_INFORMATION)}
            >
              <h2 className="step-counter">{STEPS.ITEM_INFORMATION}</h2>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h1 className="step-name">Listing Information</h1>
                {NeedHelp(STEPS.ITEM_INFORMATION)}
              </div>
            </div>
            <div className={`bar ${step >= STEPS.MEDIA ? 'active' : ''}`} />
            <div
              className={`stepper-item ${step >= STEPS.MEDIA ? 'active' : ''}`}
              onClick={() => canChangeStep(STEPS.MEDIA)}
            >
              <h2 className="step-counter">{STEPS.MEDIA}</h2>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h1 className="step-name">Media</h1>
                {NeedHelp(STEPS.MEDIA)}
              </div>
            </div>
            <div
              className={`bar ${step >= STEPS.SPECIFICATIONS ? 'active' : ''}`}
            />
            <div
              className={`stepper-item ${
                step >= STEPS.SPECIFICATIONS ? 'active' : ''
              }`}
              onClick={() => canChangeStep(STEPS.SPECIFICATIONS)}
            >
              <h2 className="step-counter">{STEPS.SPECIFICATIONS}</h2>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h1 className="step-name">Specifications</h1>
                {NeedHelp(STEPS.SPECIFICATIONS)}
              </div>
            </div>
            <div className={`bar ${step >= 5 ? 'active' : ''}`} />
            <div
              className={`stepper-item ${
                step >= STEPS.COMBINATIONS_TABLE ? 'active' : ''
              }`}
              onClick={() => canChangeStep(STEPS.COMBINATIONS_TABLE)}
            >
              <h2 className="step-counter">{STEPS.COMBINATIONS_TABLE}</h2>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h1 className="step-name">Combinations</h1>
                {NeedHelp(STEPS.COMBINATIONS_TABLE)}
              </div>
            </div>
            <div className={`bar ${step >= STEPS.SHIPPING ? 'active' : ''}`} />
            <div
              className={`stepper-item ${
                step >= STEPS.SHIPPING ? 'active' : ''
              }`}
              onClick={() => canChangeStep(STEPS.SHIPPING)}
            >
              <h2 className="step-counter">{STEPS.SHIPPING}</h2>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h1 className="step-name">Shipping</h1>
                {NeedHelp(STEPS.SHIPPING)}
              </div>
            </div>
            <div className={`bar ${step >= STEPS.PREVIEW ? 'active' : ''}`} />
            <div
              className={`stepper-item ${
                step >= STEPS.PREVIEW ? 'active' : ''
              }`}
              onClick={() => canChangeStep(STEPS.PREVIEW)}
            >
              <h2 className="step-counter">{STEPS.PREVIEW}</h2>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h1 className="step-name">Preview</h1>
                {NeedHelp(STEPS.PREVIEW)}
              </div>
            </div>
          </div>
        </CardStyled>
        {step === STEPS.ITEM_INFORMATION && <Information addStep={addStep} />}
        {step === STEPS.MEDIA && (
          <Media
            currentStore={currentStore}
            remove={removeStep}
            addStep={addStep}
            s3Repository={s3Repository}
            filesRepository={filesRepository}
            actionGetStoreImages={actionGetStoreImages}
          />
        )}
        {step === STEPS.SPECIFICATIONS && (
          <Specifications
            currentStore={currentStore}
            actionGetStoreImages={actionGetStoreImages}
            remove={removeStep}
            addStep={addStep}
            s3Repository={s3Repository}
            filesRepository={filesRepository}
          />
        )}
        {step === STEPS.COMBINATIONS_TABLE && (
          <CombinationsTable
            currentStore={currentStore}
            remove={removeStep}
            addStep={addStep}
            s3Repository={s3Repository}
            filesRepository={filesRepository}
          />
        )}
        {step === STEPS.SHIPPING && (
          <Shipping remove={removeStep} addStep={addStep} />
        )}
        {step === STEPS.PREVIEW && (
          <Preview remove={removeStep} requestPublish={requestPublish} />
        )}
      </div>
    </Wrapper>
  );
};

export default CreateItem;
